import { ListStyled, StyledListItemButton } from './styled';
import { useSidebar, useSidebarNavItemCollapse } from '../../context';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ListItemText from '@mui/material/ListItemText';
import { NavItemType } from '../../type';
import SidebarNavItem from '../SidebarNavItem';
import { SidebarNavItemCollapseProps } from '../../props';
import { Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useLocation } from 'react-router-dom';
import ListItemIcon from '@mui/material/ListItemIcon';
import Badge from '@mui/material/Badge';

const Menus = ({ menu, level, parentId }: SidebarNavItemCollapseProps) => {
  const { isSideBarOpen } = useSidebar();

  return menu.children?.map((item) => {
    switch (item.type) {
      case 'collapse':
        return <SidebarNavItemCollapse key={item.id} menu={item} level={level + 1} parentId={parentId} />;
      case 'item':
        return <SidebarNavItem key={item.id} item={item} level={level + 1} isSideBarOpen={isSideBarOpen} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });
};

const MenuIcon = ({ menu, activeModule, level }: { menu: NavItemType; activeModule: boolean; isSideBarOpen: boolean; level: number }) => {
  const iconSelectedColor = 'white';

  if (menu.iconSelected && activeModule) {
    const IconSelected = menu.iconSelected;
    return <IconSelected strokeWidth={1.5} size="40px" sx={{ color: iconSelectedColor }} />;
  }

  if (menu.icon) {
    const Icon = menu.icon;
    return <Icon strokeWidth={1.5} size="40px" sx={{ color: iconSelectedColor }} />;
  }

  return <FiberManualRecordIcon sx={{ width: 40, height: 40, color: iconSelectedColor }} fontSize={level > 0 ? 'inherit' : 'medium'} />;
};

const SidebarNavItemCollapse = ({ menu, level, parentId }: SidebarNavItemCollapseProps) => {
  const { pathname } = useLocation();
  const { isSideBarOpen, setIsSideBarOpen } = useSidebar();
  const { currentMenuActive, setCurrentMenuActive } = useSidebarNavItemCollapse();
  const navigate = useNavigate();

  const activeModule = menu.url ? pathname === menu.url || pathname.startsWith(menu.url) : false;

  useEffect(() => {
    if (pathname.startsWith(menu.url!)) {
      setCurrentMenuActive(menu.id ?? null);
    }
  }, [pathname, setCurrentMenuActive]);

  const iconSelectedColor = 'white';

  const handleItemClick = () => {
    if (!menu.children || menu.children.length === 0) {
      if (menu.url) {
        navigate(menu.url);
      }
    } else {
      setIsSideBarOpen(true);
      setCurrentMenuActive(menu.id ?? null);
    }
  };

  return (
    <>
      <Tooltip title={!isSideBarOpen ? menu.title : ''} placement="right" arrow>
        <StyledListItemButton
          level={level}
          isSideBarOpen={isSideBarOpen}
          iconSelectedColor={iconSelectedColor}
          selected={activeModule}
          onClick={handleItemClick}
          sx={{
            position: 'relative', // Ensure relative positioning for absolute badge
          }}
        >
          <ListItemIcon
            sx={{
              border: 'none',
              minWidth: 20,
              padding: isSideBarOpen ? 0 : '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              mr: isSideBarOpen ? 1 : 0,
            }}
          >
            <MenuIcon menu={menu} activeModule={activeModule} isSideBarOpen={isSideBarOpen} level={level} />
          </ListItemIcon>

          {isSideBarOpen && (
            <ListItemText
              primary={
                <Typography
                  noWrap
                  overflow="hidden"
                  textOverflow="ellipsis"
                  variant={'body1'}
                  sx={{ color: iconSelectedColor }}
                  width={'100%'}
                  fontWeight={activeModule ? 'bold' : 'normal'}
                  fontSize={'16px'}
                >
                  {menu.title as string}
                </Typography>
              }
              sx={{ flex: 'none' }}
              secondary={
                menu.caption && (
                  <Typography variant="caption" sx={{ color: iconSelectedColor }} display="block" gutterBottom>
                    {menu.caption}
                  </Typography>
                )
              }
            />
          )}

          {/* Notification Badge */}
          {menu.notification && (
            <Box
              sx={{
                ml: isSideBarOpen ? 'auto' : 0,
                mr: isSideBarOpen ? 1 : 0,
                position: isSideBarOpen ? 'relative' : 'absolute',
                top: isSideBarOpen ? 'auto' : '-8px',
                right: isSideBarOpen ? 'auto' : '2px',
              }}
            >
              <Badge
                badgeContent={menu.notification}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#FF4444',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    minWidth: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    zIndex: 10000,
                  },
                }}
              />
            </Box>
          )}

          {/* {isSideBarOpen && (
            <Box>
              {currentMenuActive === menu.id ? (
                <IconChevronUp
                  stroke={1.5}
                  size="16px"
                  style={{
                    color: 'white',
                    marginTop: 'auto',
                    marginBottom: 'auto',
                  }}
                />
              ) : (
                <IconChevronDown
                  stroke={1.5}
                  size="16px"
                  style={{
                    color: 'white',
                    marginTop: 'auto',
                    marginBottom: 'auto',
                  }}
                />
              )}
            </Box>
          )} */}
        </StyledListItemButton>
      </Tooltip>

      {/* Only show collapse if there are actual children */}
      {menu.children && menu.children.length > 0 && (
        <Collapse in={currentMenuActive === menu.id} timeout="auto" unmountOnExit>
          {currentMenuActive === menu.id && (
            <ListStyled disablePadding>
              <Menus menu={menu} level={level} parentId={parentId} />
            </ListStyled>
          )}
        </Collapse>
      )}
    </>
  );
};

export default SidebarNavItemCollapse;
